import { css } from 'emotion';
import React from 'react';
import TextInput from '../../textInput/TextInput';
import { useTheme } from '../../theme-context';
import { IAddon } from '../types';

interface IMultipleAddonParams {
    addon: IAddon;
    setAddonQuantityAction: (id: string, quantity: string) => void;
    syncCart: () => void;
}

const MultipleAddon = ({
    addon,
    setAddonQuantityAction,
    syncCart,
}: IMultipleAddonParams) => {
    const { name, addons } = addon;
    const { getFont } = useTheme();

    return (
        <div data-testid="multiple-addon-checkout-line">
            <p
                data-testid="name"
                className={css({
                    marginTop: '16px',
                    fontFamily: getFont('desktop/body/medium/bold').fontFamily,
                    fontWeight: getFont('desktop/body/medium/bold').fontWeight,
                    fontSize: getFont('desktop/body/medium/bold').fontSize,
                    // marginBottom: '4px',
                })}
            >
                {name}
            </p>
            <div
                className={css({
                    marginLeft: '12px',
                })}
            >
                {addons?.map(
                    ({ id = '', name = '', price = '', quantity = '0' }) => {
                        const content = (
                            <SingleAddon
                                key={id}
                                addon={{
                                    id,
                                    name,
                                    price,
                                    quantity,
                                }}
                                isChild={true}
                                setAddonQuantityAction={setAddonQuantityAction}
                                syncCart={syncCart}
                            />
                        );

                        return content;
                    },
                )}
            </div>
        </div>
    );
};

interface ISingleAddonParams {
    addon: IAddon;
    isChild?: boolean;
    setAddonQuantityAction: (id: string, quantity: string) => void;
    syncCart: () => void;
}

const SingleAddon = ({
    addon,
    isChild,
    setAddonQuantityAction,
    syncCart,
}: ISingleAddonParams) => {
    const { id, name, price, quantity } = addon;

    const nameFont = isChild
        ? 'desktop/body/medium'
        : 'desktop/body/medium/bold';
    const { getFont } = useTheme();

    return (
        <div data-testid="addon-checkout-line">
            <div
                className={css({
                    display: 'grid',
                    gridTemplateColumns: 'auto fit-content(100%)',
                    alignContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px',
                })}
            >
                <div>
                    <p
                        data-testid="name"
                        className={css({
                            fontFamily: getFont(nameFont).fontFamily,
                            fontWeight: getFont(nameFont).fontWeight,
                            fontSize: getFont(nameFont).fontSize,
                        })}
                    >
                        {name}
                    </p>
                    <p data-testid="price" className="eds-text-bm">
                        {price}
                    </p>
                </div>
                <div
                    data-testid="quantity"
                    className={css({
                        float: 'left',
                        alignSelf: 'center',
                        width: '120px',
                        justifySelf: 'self-end',
                        textAlign: 'right',
                        fontFamily: getFont('desktop/body/medium').fontFamily,
                        fontWeight: getFont('desktop/body/medium').fontWeight,
                        fontSize: getFont('desktop/body/medium').fontSize,
                    })}
                >
                    <TextInput
                        placeholder="0"
                        onChange={(value) => setAddonQuantityAction(id, value)}
                        onBlur={() => syncCart()}
                        value={(quantity || '').toString()}
                    />
                </div>
            </div>
        </div>
    );
};

interface IAddonCheckoutLine {
    addon: IAddon;
    setAddonQuantityAction: (id: string, quantity: string) => void;
    syncCart: () => void;
}

const AddonCheckoutLine = ({
    addon,
    setAddonQuantityAction,
    syncCart,
}: IAddonCheckoutLine) => {
    const { id, name, addons = [] } = addon;

    const Component =
        addons && addons.length > 0 ? (
            <MultipleAddon
                addon={{
                    id,
                    name,
                    addons,
                }}
                setAddonQuantityAction={setAddonQuantityAction}
                syncCart={syncCart}
            />
        ) : (
            <SingleAddon
                addon={addon}
                setAddonQuantityAction={setAddonQuantityAction}
                syncCart={syncCart}
            />
        );

    return Component;
};

export default AddonCheckoutLine;
